/**
 * Cross-tab session coordination via BroadcastChannel.
 *
 * Replaces the unreliable `localStorage` 'storage' event for logout detection.
 * BroadcastChannel is purpose-built for cross-tab messaging and doesn't conflict
 * with localStorage.clear() during logout.
 *
 * Pattern ported from participant-ui's sessionCoordinator.ts.
 */

const CHANNEL_NAME = 'wealthlane_session';

export type SessionEventType = 'new_session_event' | 'logout_event';

export interface SessionEvent {
  type: SessionEventType;
}

/**
 * Persistent listener channel — used exclusively for receiving messages.
 * Kept separate from the throw-away sender so the self-exclusion guarantee
 * of BroadcastChannel isn't inadvertently relied upon across page navigations.
 */
export const sessionChannel = new BroadcastChannel(CHANNEL_NAME);

/**
 * Broadcasts logout to all other tabs and closes the listener so this tab
 * never receives its own message. Unlike new_session_event, we close the
 * listener because authService.logout() navigates away immediately after.
 */
export function broadcastLogout(): void {
  sessionChannel.close();

  const sender = new BroadcastChannel(CHANNEL_NAME);
  sender.postMessage({ type: 'logout_event' } satisfies SessionEvent);
  sender.close();
}

/**
 * Handles an incoming BroadcastChannel message.
 * Returns the event type if the caller should act on it, or null if invalid.
 */
export function handleSessionMessage(event: MessageEvent): SessionEventType | null {
  const type = (event.data as SessionEvent | undefined)?.type;
  if (type !== 'logout_event' && type !== 'new_session_event') return null;
  return type;
}
